import { Link } from 'react-router-dom';

import { buildIntegrationPrompt } from '@admin-platform/shared/integration-content';
import { AnchorHeading } from '../components/AnchorHeading';
import { UiPath } from '../components/UiPath';
import { useDocsPage } from '../hooks/useDocsPage';

const AGENT_PROMPT = buildIntegrationPrompt('productEvents');

const SERVER_SEND = `// Anywhere on your server, when something worth knowing happens.
// The key lives in your secret store, never in a page or an app bundle.
await fetch('https://api.revvly.ai/api/v1/product-events', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.REVVLY_PRODUCT_EVENTS_KEY}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    events: [
      {
        eventId: \`export-\${report.id}\`, // the same id again is DUPLICATE: retries are safe
        event: 'report.exported',
        userId: user.id, // your own id for the person
        clientId: user.accountId, // your own id for their company = the client's External ID
        properties: { format: 'pdf' }, // flat values only; send ids, not personal data
      },
    ],
  }),
});
// 202 { "results": [ { "eventId": "export-812", "status": "ACCEPTED" } ] }`;

const TOKEN_ROUTE = `// server.js (Express). Same key as above; it mints, the page never sees it.
import express from 'express';

const app = express();

// requireSignedInUser is your own auth middleware.
app.get('/api/revvly-token', requireSignedInUser, async (req, res) => {
  const user = req.user; // from YOUR session, never from the request
  const response = await fetch('https://api.revvly.ai/api/v1/product-events/tokens', {
    method: 'POST',
    headers: {
      Authorization: \`Bearer \${process.env.REVVLY_PRODUCT_EVENTS_KEY}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId: user.id, clientId: user.accountId, ttlSeconds: 900 }),
  });
  if (!response.ok) return res.status(502).end();
  const { token } = await response.json();
  res.type('text/plain').send(token); // the script asks again before it expires
});`;

const PAGE_SNIPPET = `<!-- Before </body> on your signed-in pages.
     Served by the Revvly dashboard, so there is no package to install. -->
<script src="https://app.revvly.ai/revvly-events.js"></script>
<script>
  RevvlyEvents.init({
    getToken: () => fetch('/api/revvly-token').then((r) => r.text()),
    respectDoNotTrack: true,
  });

  // Page views and time on each page are recorded for you. Add your own:
  RevvlyEvents.track('report.exported', { format: 'pdf' });
</script>`;

const CSP_EVENTS = `Content-Security-Policy:
  script-src 'self' https://app.revvly.ai 'nonce-<per-request-nonce>';
  connect-src 'self' https://api.revvly.ai;

<!-- The inline init script then carries the same nonce -->
<script nonce="<per-request-nonce>">
  RevvlyEvents.init({ getToken: () => fetch('/api/revvly-token').then((r) => r.text()) });
</script>`;

export function ProductEventsSetupGuide() {
  useDocsPage('Set up product event tracking — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Set up product event tracking</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Set up <em>product event</em> tracking.
        </h1>
        <p className="dp-oneliner">
          From nothing to each client&rsquo;s usage on their Revvly page: switch it on, issue a key,
          tell Revvly who your clients are, send events from your backend and, if you want page
          views and time on page, add the browser script. Nothing sent this way is billed.
        </p>
      </header>

      <section className="dp-section">
        <AnchorHeading id="prompt">Give this prompt to an integration agent</AnchorHeading>
        <p className="dp-section-sub">
          It covers every step on this page. Copied from the dashboard (Settings, Product events,
          Setup guide) it also carries your tenant id.
        </p>
        <pre className="api-code">{AGENT_PROMPT}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="how-it-works">How the pieces fit</AnchorHeading>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Your backend</div>
            <div className="dp-field-v">
              Holds an API key and sends what only the server knows (a report was exported, a
              payment ran). It can also ask Revvly for a short-lived token for the person who is
              signed in.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">The browser script</div>
            <div className="dp-field-v">
              Optional. One script served by Revvly that records pages and time on page. It never
              holds the key: it sends with the token your backend handed it, and only from the sites
              you list.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Revvly</div>
            <div className="dp-field-v">
              Matches each event to a client by its External ID, keeps it for 13 months and shows it
              on the client&rsquo;s Usage tab, in Product usage in the Strategy menu and in the
              support ticket of the person who wrote in.
            </div>
          </div>
        </div>
        <p className="dp-plain">
          There is no package to install or upgrade. The server half is one HTTP call. The browser
          half is a script that Revvly serves from the dashboard (app.revvly.ai), so a page loads
          the current version every time and nothing on your side goes out of date. If you copy the
          file onto your own host instead, you own keeping it current.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="enable">Step 1: Ask Revvly to switch Product usage on</AnchorHeading>
        <p className="dp-section-sub">
          Product usage is an add-on that Revvly switches on for your workspace; it is not a setting
          you flip yourself. When it is on, a Product events page appears under Settings, Product
          usage appears in the Strategy menu and the key form below gains the tick you need. If you
          cannot see them, ask Revvly to turn it on.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="key">Step 2: Issue an API key that can send events</AnchorHeading>
        <UiPath
          steps={['Settings', 'API Keys', 'Issue key', 'Can send product events']}
          note="Admins and owners. The tick only appears once Product usage is on, and no existing key has it. The key is shown once: put it in your server's secret store, e.g. REVVLY_PRODUCT_EVENTS_KEY."
        />
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            <strong>This is not the usage-metering key.</strong> Product events and billable usage
            are separate pipelines with separate permissions, so a key for one cannot call the
            other. Issue a key with only the tick you need.{' '}
            <strong>The key never goes in a page</strong>, an app bundle, logs, URLs or git.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="clients">Step 3: Tell Revvly who your clients are</AnchorHeading>
        <UiPath
          steps={['Clients', 'a client', 'Edit Client', 'External ID']}
          note="Your own id for that company, unique in your workspace."
        />
        <p className="dp-section-sub">
          An event names the person and the company in your own ids. The company id (
          <span className="api-inline">clientId</span>) is matched to the client&rsquo;s External
          ID, so set each one to what your product already calls that company. An event whose{' '}
          <span className="api-inline">clientId</span> matches no client is still stored, but it
          shows on no client. Clients you create through the API can carry one from the start; see{' '}
          <Link to="/docs/guides/external-id">External ID integration</Link>.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="server">Step 4: Send events from your backend</AnchorHeading>
        <p className="dp-section-sub">
          One call carries 1 to 100 events. Each needs an event name and at least one of{' '}
          <span className="api-inline">userId</span> and{' '}
          <span className="api-inline">clientId</span>. Send an{' '}
          <span className="api-inline">eventId</span> of your own so a retry is recognised and
          stored once. One bad event is refused on its own and never sinks the others.
        </p>
        <pre className="api-code">{SERVER_SEND}</pre>
        <p className="dp-plain">
          Keep names stable and readable (<span className="api-inline">report.exported</span>, not a
          sentence with a date in it): each new name becomes a line in Settings, Product events that
          someone has to organise. Every field and limit is in the{' '}
          <Link to="/docs/api/product-events">Product Events API reference</Link>.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            <strong>Send ids, not personal data.</strong> A property named like a credential
            (password, token, secret, card number) and any value that is an email address, a JWT or
            a card number is stored as <span className="api-inline">[redacted]</span>. Properties
            are flat: strings up to 500 characters, numbers, booleans or null.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="browser">
          Step 5: Add page views and time on page (optional)
        </AnchorHeading>
        <p className="dp-section-sub">
          Skip this if server events are enough. The browser half has three parts.
        </p>

        <h3 className="api-sub-heading">5a. A token route on your backend</h3>
        <p className="dp-section-sub">
          One route your signed-in pages call. It reads the person from your own session, never from
          the request, and asks Revvly for a token for them. The token lasts 15 minutes by default
          (60 to 3600 seconds); the script asks again before it expires. Revoking the key stops
          every token it minted.
        </p>
        <pre className="api-code">{TOKEN_ROUTE}</pre>

        <h3 className="api-sub-heading">5b. List the sites that may send</h3>
        <UiPath
          steps={['Settings', 'Product events', 'Browser tracking', 'Allowed sites']}
          note="Admins only. One entry per line: scheme and host, for example https://app.example.com, plus http://localhost:3000 for development."
        />
        <p className="dp-section-sub">
          This is the second check: a token handed to a page on any other site is refused with a
          403. For many subdomains write <span className="api-inline">https://*.example.com</span>:
          it covers every subdomain at any depth but not{' '}
          <span className="api-inline">example.com</span> itself, another scheme or another port, so
          list the bare domain on its own line. A white-label domain is always its own line. Up to
          100 lines.
        </p>

        <h3 className="api-sub-heading">5c. Load the script and start it</h3>
        <p className="dp-section-sub">
          The script is served by the Revvly dashboard, at{' '}
          <span className="api-inline">https://app.revvly.ai/revvly-events.js</span>. It records{' '}
          <span className="api-inline">page.viewed</span> on load and on every single-page
          navigation and <span className="api-inline">page.left</span> with the seconds the page was
          actually visible. Ids in paths become <span className="api-inline">:id</span>, and the
          query string, hash and page title are never sent.
        </p>
        <pre className="api-code">{PAGE_SNIPPET}</pre>
        <p className="dp-plain">
          Options, privacy controls (<span className="api-inline">optOut</span>, Do Not Track) and
          delivery behaviour are in the{' '}
          <Link to="/docs/api/product-events">Browser SDK section</Link> of the reference. The
          script keeps a session id in the browser&rsquo;s local storage, so say so in your own
          privacy notice and honour your visitors&rsquo; choices.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="csp">If your site sets a Content Security Policy</AnchorHeading>
        <p className="dp-section-sub">
          Only the browser half is affected: events sent from your backend never meet a CSP. If your
          pages send a <span className="api-inline">Content-Security-Policy</span> header or meta
          tag, allow two things. Without them the browser blocks the script or the request, and the
          console names the directive that refused it.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">script-src</div>
            <div className="dp-field-v">
              <span className="api-inline">https://app.revvly.ai</span>, where the script is loaded
              from. If you copy the file onto your own host instead, your own origin already covers
              it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">connect-src</div>
            <div className="dp-field-v">
              <span className="api-inline">https://api.revvly.ai</span>, where the script sends
              events. If you set a different <span className="api-inline">apiUrl</span>, allow that
              host instead. Your own token route is on your own origin.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">The inline snippet</div>
            <div className="dp-field-v">
              A strict policy refuses inline scripts, including the{' '}
              <span className="api-inline">RevvlyEvents.init</span> call. Give that script your
              per-request nonce or its hash, or put the call in a file you already serve.
            </div>
          </div>
        </div>
        <pre className="api-code">{CSP_EVENTS}</pre>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            <strong>Allowed sites and a CSP are two separate checks.</strong> Allowed sites (Step
            5b) is Revvly refusing a token from a page it does not know; a CSP is your
            visitors&rsquo; browsers refusing to talk to Revvly. A page needs to pass both. The
            script sets no cookies and uses only local storage, so no cookie rule is involved.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="name">Step 6: Give the events names</AnchorHeading>
        <UiPath
          steps={['Settings', 'Product events']}
          note="Admins only. New names wait under New until someone organises them."
        />
        <p className="dp-section-sub">
          Open an event, give it a readable name and a category, and tie it to the feature, module
          or bundle it shows use of. That mapping is what turns a list of event names into the
          Feature set card on each client. Leave <span className="api-inline">page.viewed</span> and{' '}
          <span className="api-inline">page.left</span> as they are: they feed the session views.
          Suggest from documentation proposes mappings from your knowledge base; nothing is applied
          until you accept it. See{' '}
          <Link to="/docs/guides/product-usage">See how clients use your product</Link>.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="verify">Step 7: Check it works</AnchorHeading>
        <p className="dp-section-sub">
          Trigger something once, then open the client&rsquo;s Usage tab (Clients, the client,
          Usage). Events appear within a few seconds; the Feature set card and the overview use a
          nightly roll-up, so they fill the next day.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">No tick on the key form</div>
            <div className="dp-field-v">
              Product usage is not on for your workspace yet (Step 1).
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">403 on the key route</div>
            <div className="dp-field-v">
              The key lacks Can send product events, or Product usage is off.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">403 from the browser</div>
            <div className="dp-field-v">
              The page&rsquo;s origin is not under Allowed sites, or an event names a different user
              or client than the token.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">401 from the browser</div>
            <div className="dp-field-v">
              The token expired or its key was revoked; the script asks again.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">503 when minting</div>
            <div className="dp-field-v">
              Browser tokens are not set up on Revvly&rsquo;s side; contact Revvly.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Events stored, shown on no client</div>
            <div className="dp-field-v">
              The clientId matches no client&rsquo;s External ID (Step 3).
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">A REJECTED result</div>
            <div className="dp-field-v">
              Its reason says what to fix; the other events in the call were kept.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="checklist">Before you go live</AnchorHeading>
        <ol className="dp-connects">
          <li>The key is only on your server: not in the page, logs, URLs or git.</li>
          <li>The token route mints only for the person your own session signed in.</li>
          <li>Every production site is under Allowed sites; the bare domain is listed too.</li>
          <li>
            If your pages send a Content Security Policy, it allows the script address and{' '}
            <span className="api-inline">https://api.revvly.ai</span>.
          </li>
          <li>Every client you track has an External ID equal to the clientId you send.</li>
          <li>Events carry ids and flat properties, no personal data.</li>
          <li>
            Your privacy notice says you measure product use, and you honour Do Not Track and an
            opt-out.
          </li>
          <li>
            You know how to erase one person&rsquo;s events on request (the erase call in the
            reference).
          </li>
        </ol>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/api/product-events">Product Events API reference ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/product-usage">See how clients use your product ›</Link>
          </li>
          <li>
            <Link to="/docs/reference/api-keys">API key capabilities ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
